import { createStore, createEffect } from "effector"
import { useUnit } from "effector-react"

import { UserStorageKey } from "modules/UserStorage/constants"
import UserStorageService from "modules/UserStorage/services/UserStorageService"

// Enumerate reservation sidebar fields that can be pinned. Use valueTestIds from ReservationSidebarDetails for consistency.
export enum ReservationField {
    address = "reservation-address",
    coordinates = "reservation-coordinates",
    targetCountry = "reservation-target-country",
    shippingDetails = "reservation-shipping-details",
    readyForShipping = "reservation-forwarded-to-shipping",
    period = "reservation-period",
    purpose = "reservation-purpose",
    warehouse = "reservation-warehouse",
    profitCenter = "reservation-profit-center",
    markAsRentToBuy = "reservation-mark-as-rent-to-buy",
    relatedRequestForSale = "reservation-related-request-for-sale",
    customerNumber = "reservation-customer-number",
    crmReferenceNumber = "reservation-crm-reference-number",
    customerName = "reservation-customer-name",
    customerAddress = "reservation-customer-address",
    language = "reservation-language",
    currency = "reservation-currency",
    contactPerson = "reservation-contact-person",
    customerPhone = "reservation-customer-phone",
    customerEmail = "reservation-customer-email",
    salesPerson = "reservation-sales-person",
    salesAdmin = "reservation-sales-admin",
    customerOrderNumber = "reservation-customer-order-number",
    customerOrderDate = "reservation-customer-order-date",
    commission = "reservation-commission",
    comments = "reservation-comments",
}

// Stores
// Store now accepts any string id (enum values + dynamic row ids)
const $reservationPinnedFields = createStore<string[] | null>(null)

// Effects
const fetchPinnedFields = createEffect(() => {
    return UserStorageService.getValue(UserStorageKey.reservationSidebarPinnedFields)
})

const pinField = createEffect(async (field: string) => {
    const pinnedFields = $reservationPinnedFields.getState()
    if (!pinnedFields || pinnedFields.includes(field)) return pinnedFields
    const newPinnedFields = [...pinnedFields, field]
    await UserStorageService.setValue(UserStorageKey.reservationSidebarPinnedFields, newPinnedFields)
    return newPinnedFields
})

const unpinField = createEffect(async (field: string) => {
    const pinnedFields = $reservationPinnedFields.getState()
    if (!pinnedFields || !pinnedFields.includes(field)) return pinnedFields
    const newPinnedFields = pinnedFields.filter((f) => f !== field)
    await UserStorageService.setValue(UserStorageKey.reservationSidebarPinnedFields, newPinnedFields)
    return newPinnedFields
})

const toggleFieldPin = createEffect(async (field: string) => {
    const pinnedFields = $reservationPinnedFields.getState()
    if (pinnedFields?.includes(field)) return unpinField(field)
    return pinField(field)
})

// Listeners
// Accept any stored string ids (backwards compatible)
$reservationPinnedFields.on(fetchPinnedFields.doneData, (state, payload) => {
    if (payload.status === "unreadable") return state
    return payload.status === "empty" ? [] : payload.value
})

$reservationPinnedFields.on(pinField.doneData, (_, payload) => payload)
$reservationPinnedFields.on(unpinField.doneData, (_, payload) => payload)

// Hook
export function useReservationPinnedFields() {
    return {
        pinnedFields: useUnit($reservationPinnedFields),
        fetchPinnedFields,
        pinField,
        unpinField,
        toggleFieldPin,
    }
}

export default {
    useReservationPinnedFields,
}
